/**
 * Cross-fade view-transition wrapper for Dash drill flows.
 *
 * Uses the native `document.startViewTransition()` API when available
 * (Chrome 111+, Edge 111+, Safari 18+) so the browser cross-fades the
 * before/after DOM automatically. Falls back to a microtask-batched
 * direct call when the API is missing — no animation, but the state
 * update still happens.
 *
 * The transition is intentionally tiny (<200ms): drill-through should
 * feel "snappy + acknowledged", not animated. Longer transitions push
 * the user into "did anything happen?" territory.
 *
 * Why this lives in utils/ instead of being baked into the §1.6 drillTo
 * handler: any state mutation can opt in by wrapping its setter call,
 * not just navigation. Tile expand, filter chip swap, drill-back —
 * same primitive.
 *
 * @example
 *   await withViewTransition("drill-into-customer", () => {
 *     setCurrentDashboardId(targetId)
 *   })
 */

const PREFERS_REDUCED_MOTION =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

export interface ViewTransition {
  ready: Promise<void>
  finished: Promise<void>
  skipTransition: () => void
}

/**
 * Minimal subset of the View Transitions API that we care about.
 * Cast `document` to this when calling — TypeScript's lib doesn't
 * always include the type yet (varies by ts-lib version).
 */
interface DocumentWithViewTransitions {
  startViewTransition?: (callback: () => void | Promise<void>) => ViewTransition
}

/**
 * Run `callback` inside a browser view transition where supported.
 * Always invokes the callback exactly once. Returns a promise that
 * resolves when the transition's animations complete (or immediately
 * when no transition was started).
 *
 * Honours `prefers-reduced-motion: reduce` — when the user has opted
 * out of motion, this becomes a direct call with no animation.
 *
 * @param _name  Reserved for future per-transition styling hooks; the
 *               native API doesn't accept a name yet, but having one
 *               at the call site makes the intent obvious in code
 *               review and lets us layer custom CSS later.
 * @param callback  Mutates whatever state should be transitioned —
 *                  React re-renders inside the transition's "before"
 *                  capture window are coalesced with the "after"
 *                  capture for a single cross-fade.
 */
export async function withViewTransition(
  _name: string,
  callback: () => void | Promise<void>
): Promise<void> {
  if (typeof document === "undefined") {
    await callback()
    return
  }

  if (PREFERS_REDUCED_MOTION) {
    await callback()
    return
  }

  const doc = document as unknown as DocumentWithViewTransitions
  const start = doc.startViewTransition
  if (typeof start !== "function") {
    // Older browser — just run the callback. The state still updates,
    // just without the cross-fade.
    await callback()
    return
  }

  const transition = start.call(doc, callback)
  // `finished` resolves when CSS animations end; we await it so the
  // returned promise chains naturally with subsequent UI work.
  try {
    await transition.finished
  } catch {
    // The transition may be skipped or interrupted (e.g. another
    // navigation fires before this one's animations complete). Swallow
    // — the state mutation already happened inside the callback.
  }
}

/**
 * Test-only export: lets unit tests verify the reduced-motion branch
 * without messing with the global matchMedia mock.
 */
export const _internals = {
  isReducedMotion: () => PREFERS_REDUCED_MOTION,
}
